import { ENABLE_TEMPLATE_VALIDATION_BEFORE_SIGNING } from "constants/featureFlags";
import canElementBeRequired, {
  canElementHaveInitials,
} from "./elementTypeUtils";

function isValueEmpty(
  element: TemplateElement,
  value: TemplateDataValue,
  signatureValue?: SignatureData,
) {
  if (
    canElementHaveInitials(element) &&
    element.requireInitials &&
    !signatureValue?.signature
  ) {
    return true;
  }

  switch (element.type) {
    case "paragraph":
    case "short-answer":
      return !value || value === "<p><br></p>";
    case "short-answer-group":
      return !value || element.items.some((item) => !value?.[item.id]);
    case "radios":
      if (!element.options.length && !element.enableOtherOption) {
        return false; // Someone might be using this type with no options specified,
        // like a text-only field - see OIM-2479
      }
      return !value?.selectedOption && !value?.otherOption;
    case "checkboxes":
      if (!element.options.length && !element.enableOtherOption) {
        return false; // Someone might be using this type with no options specified,
        // like a text-only field - see OIM-2479
      }
      return !value?.selectedOptions?.length && !value?.otherOption;
    case "radios-grid":
      return element.rows.some((row) => !value?.[row.title]);
    case "checkboxes-grid":
      return element.rows.some((row) => !value?.[row.title]?.length);
    case "signature":
      return !signatureValue?.signature;
    case "text-only":
      return false; // can only be required if it has initials
    default:
      return !value;
  }
}

export default function getMissingRequiredFields(
  elements: TemplateElement[],
  data: TemplateData,
) {
  if (!ENABLE_TEMPLATE_VALIDATION_BEFORE_SIGNING) {
    return [];
  }
  const elementsFlat = elements.flatMap((element) =>
    element.type === "section" ? element.children : element,
  );

  const dataFlat = Object.fromEntries(
    elements.flatMap((element) => {
      if (element.type === "section") {
        return element.children.map((child) => [
          child.id,
          data?.[element.id]?.[child.id],
        ]);
      }
      return [[element.id, data?.[element.id]]];
    }),
  );

  return elementsFlat
    .filter(
      (element) =>
        canElementBeRequired(element) &&
        element.isRequired &&
        isValueEmpty(
          element,
          dataFlat[element.id],
          data.signatures?.[element.id],
        ),
    )
    .map((element) => element.id);
}

export function isTemplateContentValid(
  elements: TemplateElement[],
  data: TemplateData,
) {
  if (!ENABLE_TEMPLATE_VALIDATION_BEFORE_SIGNING) {
    return true;
  }
  return getMissingRequiredFields(elements, data).length === 0;
}
